import { createTheme, PaletteOptions } from '@mui/material/styles';
import { APP_FONT_WEIGHT_MEDIUM } from '@common/styles/fonts';

// Every theme in this file states the family. MUI's default is its own Roboto
// stack, so a secondary theme that omits `typography` silently re-fonts
// whatever subtree its ThemeProvider wraps back to Roboto.
// The CUSTOM PROPERTY, not the APP_FONT_FAMILY literal, and that is
// permanent: `--app-font-family` is how the organization's `useRobotoFont`
// application config reaches the themes (see UiFontSync). Pinning this
// back to the literal would compile fine and silently ignore the config.
// The dev font dropdown rides on the same property.
// Buttons (incl. tabs) and h6 (DialogTitle) default to 500, which reads as
// Roboto Regular in Nunito Sans. Only those two variants take the
// font-dependent weight; fontWeightMedium itself is left alone so tooltips,
// badges, alerts and table heads keep their weight.
const typography = {
  fontFamily: 'var(--app-font-family)',
  button: { fontWeight: APP_FONT_WEIGHT_MEDIUM },
  h6: { fontWeight: APP_FONT_WEIGHT_MEDIUM },
} as const;

export default createTheme({
  palette: {
    primary: {
      main: '#005C7E',
      light: '#005C7E1A',
      dark: '#182025',
      contrastText: '#fff',
    },
    secondary: {
      main: '#B0BEC5',
      light: '#1bc1f3',
      dark: '#5b73f3',
      contrastText: '#fff',
    },
    success: {
      main: '#219653',
      light: '#2196531A',
    },
    warning: {
      main: '#D57200',
      light: '#E891374D',
    },
    error: {
      main: '#D5000E',
      light: '#D5000E1A',
    },
    info: {
      main: '#2196F3',
      dark: '#5C6269',
      // Hex, NOT the keyword 'whitesmoke' (same color): MUI v6's Alert builds
      // its variant styles by running darken() over EVERY palette entry that
      // has main+light, and darken() rejects CSS keywords — so a keyword here
      // crashed any <Alert> rendered under this theme (VE-24948 surfaced it;
      // the stale-version warning banners were equally affected).
      light: '#F5F5F5',
      contrastText: '#CCEBFF',
    },
    processed: {
      main: '#D57200',
    },
  } as PaletteOptions,
  typography: {
    ...typography,
    fontSize: 12,
  },
  components: {
    MuiSlider: {
      styleOverrides: {
        rail: {
          color: '#2196F3',
        },
      },
    },
  },
});

export const buttonTheme = createTheme({
  typography,
  palette: {
    primary: {
      main: '#2196F3',
      light: '#1bc1f3',
      dark: '#1976d2',
      contrastText: '#fff',
    },
    secondary: {
      main: '#B0BEC5',
      light: '#1bc1f3',
      dark: '#1976d2',
      contrastText: '#CFD8DC',
    },
    text: {
      secondary: '#48555C',
    },
  },
});

export const tabsTheme = createTheme({
  typography,
  palette: {
    primary: {
      main: '#F9A02C',
      light: '#f9d45f',
      dark: '#f99516',
      contrastText: '#fff',
    },
  },
});

export const folderTheme = createTheme({
  typography: {
    ...typography,
    // The folder view's row text, at 13px. CHOSEN, not inherited.
    //
    // The rows render `variant="caption"`, and this theme sets no
    // `fontSize`, so MUI's coefficient is 1 and caption was landing on its
    // unscaled 12px default. Nobody picked that; it is where the default
    // happened to fall. 13px is what the design renders file rows at
    // (`bodySm` in the Redact Legacy prototype), and it restores a real step
    // between the filename — the thing people scan and click — and the
    // metadata beside it, which stays at caption tier. A flat 12px gave the
    // row no hierarchy at all.
    //
    // It also costs about 8% width on a column that auto-fits to the longest
    // filename. The `nunitoSans95` font choice exists to buy that back: 13px
    // at 95% width is roughly 12.3px wide and 13px tall, which is the trade
    // this surface wants.
    //
    // Scoped to this theme on purpose. The app-wide `typography.fontSize`
    // knob does not reach here — this theme does not use the coefficient —
    // and would move MUI's variants everywhere else while leaving ~480
    // hardcoded sizes untouched. `resolveDetailsNameFont` reads
    // `caption.fontSize`, so the Name column's canvas measuring follows
    // automatically rather than needing a second edit.
    // The variable, so the folder view's size dropdown can move it at
    // runtime. The fallback is the real value: drop the dropdown and the
    // rows stay at 13px with nothing else to change.
    caption: { fontSize: 'var(--app-row-font-size, 13px)' },
  },
  palette: {
    primary: {
      main: '#005C7E',
      light: '#005C7E1A',
      dark: '#004058',
      contrastText: '#FFF',
    },
    secondary: {
      main: '#000000',
      light: '#98AFC7',
      dark: '#004058',
      contrastText: '#FFF',
    },
    info: {
      main: '#00529B',
      light: '#BDE5F8',
      dark: '#48555C',
    },
    success: {
      main: '#4F8A10',
      light: '#DFF2BF',
    },
    warning: {
      main: '#9F6000',
      light: '#FEEFB3',
    },
    error: {
      main: '#D8000C',
      light: '#FFD2D2',
    },
  },
});
